import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../services/api';
import { Search, AlertCircle, Loader, X, CheckCircle, Clock, AlertTriangle, Play } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { usePermissions } from '../context/PermissionsContext';

const getTimeElapsed = (dateStr) => {
  const created = new Date(dateStr);
  const now = new Date();
  const diffMs = now - created;
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const remHours = hours % 24;
    return { text: `${days}d ${remHours}h`, isOverdue: true };
  }
  return { text: `${hours}h ${minutes}m`, isOverdue: false };
};

const TechnicianComplaints = () => {
  const perms = usePermissions();
  const can = (key) => perms[key] !== false;
  const [searchParams, setSearchParams] = useSearchParams();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ status: '', priority: '' });
  const [resolveModal, setResolveModal] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [resolving, setResolving] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const highlightRef = useRef(null);

  const fetchComplaints = async () => {
    setLoading(true);
    try {
      let allComplaints = [];
      let nextPage = '/complaints/';
      while (nextPage) {
        const res = await api.get(nextPage);
        allComplaints = [...allComplaints, ...(res.data.results || [])];
        nextPage = res.data.next ? res.data.next.replace(api.defaults.baseURL, '') : null;
      }
      setComplaints(allComplaints);
    } catch (err) {
      setError('Failed to load complaints');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchComplaints(); }, []);

  // Check for highlight param from notification
  useEffect(() => {
    const highlight = searchParams.get('highlight');
    if (highlight) {
      setHighlightId(parseInt(highlight));
      searchParams.delete('highlight');
      setSearchParams(searchParams, { replace: true });
      setTimeout(() => setHighlightId(null), 4000);
    }
  }, [searchParams]);

  // Scroll to highlighted complaint
  useEffect(() => {
    if (highlightId && !loading && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightId, loading]);

  const handleStartProgress = async (id) => {
    setActionLoading(id);
    try {
      await api.post(`/complaints/${id}/start-progress/`);
      await fetchComplaints();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update complaint');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResolve = async () => {
    if (!resolveModal) return;
    setResolving(true);
    try {
      await api.post(`/complaints/${resolveModal.id}/resolve/`, { resolution_notes: resolutionNotes });
      await fetchComplaints();
      setResolveModal(null);
      setResolutionNotes('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resolve complaint');
    } finally {
      setResolving(false);
    }
  };

  const filteredComplaints = useMemo(() => {
    return complaints.filter(c => {
      const matchesSearch = !searchTerm ||
        c.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.customer_username?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = !filters.status || c.status === filters.status;
      const matchesPriority = !filters.priority || c.priority === filters.priority;
      return matchesSearch && matchesStatus && matchesPriority;
    });
  }, [complaints, searchTerm, filters]);

  const statusConfig = {
    open: { color: 'bg-blue-100 text-blue-700', icon: AlertCircle, label: 'Open' },
    in_progress: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'In Progress' },
    resolved: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Resolved' },
    closed: { color: 'bg-gray-100 text-gray-700', icon: X, label: 'Closed' },
  };

  const priorityConfig = {
    low: 'bg-gray-100 text-gray-700',
    medium: 'bg-blue-100 text-blue-700',
    high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
    critical: 'bg-red-100 text-red-700',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Complaints</h1>
        <p className="text-sm text-gray-500">View and manage complaints assigned to you</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertCircle size={16} className="text-red-600" />
          <span className="text-sm text-red-700">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto"><X size={14} /></button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text" value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search complaints..."
              className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>
          <select value={filters.status} onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
            className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none">
            <option value="">All Statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
          <select value={filters.priority} onChange={(e) => setFilters(f => ({ ...f, priority: e.target.value }))}
            className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none">
            <option value="">All Priorities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </div>
      </div>

      {/* Complaints List */}
      {filteredComplaints.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <CheckCircle size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No complaints found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredComplaints.map(c => {
            const StatusIcon = statusConfig[c.status]?.icon || AlertCircle;
            const time = getTimeElapsed(c.created_at);
            return (
              <div key={c.id} ref={c.id === highlightId ? highlightRef : undefined} className={`bg-white rounded-xl border p-5 hover:shadow-md transition-all ${c.id === highlightId ? 'border-blue-400 ring-2 ring-blue-200 bg-blue-50' : 'border-gray-200'}`}>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="font-semibold text-gray-900">{c.title}</h3>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig[c.status]?.color}`}>
                        <StatusIcon size={12} /> {statusConfig[c.status]?.label}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityConfig[c.priority]}`}>
                        {c.priority}
                      </span>
                      {/* Time elapsed badge */}
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${time.isOverdue ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                        <Clock size={12} /> {time.text}
                        {time.isOverdue && <AlertTriangle size={12} />}
                      </span>
                    </div>
                    {c.description && (
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{c.description}</p>
                    )}
                    <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                      <span>Customer: <strong className="text-gray-700">{c.customer_name}</strong> ({c.customer_username})</span>
                      {c.area_name && <span>Area: <strong className="text-gray-700">{c.area_name}</strong></span>}
                      {c.customer_phone && <span>Phone: <strong className="text-gray-700">{c.customer_phone}</strong></span>}
                    </div>
                  </div>

                  {/* Actions */}
                  {(c.status === 'open' || c.status === 'in_progress') && (can('can_update_complaints') || can('can_close_complaints')) && (
                    <div className="flex gap-2 flex-shrink-0">
                      {c.status === 'open' && can('can_update_complaints') && (
                        <button
                          onClick={() => handleStartProgress(c.id)}
                          disabled={actionLoading === c.id}
                          className="px-4 py-2 bg-yellow-600 text-white text-sm rounded-lg hover:bg-yellow-700 flex items-center gap-1.5 transition-colors disabled:opacity-50"
                        >
                          <Play size={14} /> Start
                        </button>
                      )}
                      {can('can_close_complaints') && (
                        <button
                          onClick={() => setResolveModal(c)}
                          className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center gap-1.5 transition-colors"
                        >
                          <CheckCircle size={14} /> Resolve
                        </button>
                      )}
                    </div>
                  )}
                  {c.status === 'resolved' && c.resolution_notes && (
                    <div className="text-xs text-gray-500 bg-green-50 p-2 rounded-lg max-w-xs">
                      <strong>Notes:</strong> {c.resolution_notes}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Resolve Modal */}
      {resolveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-bold text-gray-900">Resolve Complaint</h3>
              <button onClick={() => { setResolveModal(null); setResolutionNotes(''); }} className="p-1 hover:bg-gray-100 rounded-full">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600">
                Marking <strong>{resolveModal.title}</strong> as resolved.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Resolution Notes (optional)</label>
                <textarea
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  placeholder="Describe how the issue was resolved..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => { setResolveModal(null); setResolutionNotes(''); }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium">
                  Cancel
                </button>
                <button onClick={handleResolve} disabled={resolving}
                  className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50">
                  {resolving ? <><Loader size={14} className="animate-spin" /> Resolving...</> : <><CheckCircle size={14} /> Resolve</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TechnicianComplaints;
